import type { InvokeResult, ReduxSagaModel } from '@/models/types';
import type { Filter } from './objects/datasetViewState';
import Dashboard, {
  DashboardSettings,
  isManagedCacheMode,
  DEFAULT_CACHE_AUTO_RECENCY_DAYS,
} from '@/services/dashboard';
import type {
  CacheRefreshConfigPayload,
  CacheRefreshConfigViz,
  DashboardCacheMode,
  FilterCombination,
} from '@/services/dashboard';
import DashboardWidget from '@/services/dashboardWidget/abstractDashboardWidget';
import VisualizationDashboardWidget from '@/services/dashboardWidget/visualizationDashboardWidget';
import DashboardWidgetFactory from '@/services/dashboardWidget/dashboardWidgetFactory';
import DynamicFiltersDashboardWidget, {
  DataType,
  FilterType,
} from '@/services/dashboardWidget/dynamicFiltersDashboardWidget';
import DashboardFilterPersistence, {
  FilterOptionsMap,
} from '@/services/dashboardFilterPersistence';
import Dataset from '@/services/dataset';
import Visualization from '@/services/visualization';
import { waitForRedshiftQueryUntilDoneAndReturnResults } from '@/services/dataset.common';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { handleUserError } from '@/utils/handleError';
import { assertIsString } from '@/utils/typeChecks';
import { logger } from '@/utils/logger';
import debounce from 'lodash/debounce';
import { DASHBOARD_MAX_VISIBLE_FILTERS } from '@/utils/constants';

const ns = 'app.models.dashboard';

type DashboardFilter = Omit<Filter, 'active'> & {
  widgetId: string;
  vizId: string;
};

type RemoteFilterOption = { value: string | number | boolean | null; label: string };

export interface State {
  current: Dashboard | null;
  invokeCreateResult: InvokeResult<string> | null;
  invokeDeleteResult: InvokeResult<null> | null;
  invokeRenameResult: InvokeResult<null> | null;
  invokeDuplicateResult: InvokeResult<string> | null;
  list: InvokeResult<Dashboard[]> | null;
  invokeFetchWidgetsResult: InvokeResult<DashboardWidget[]> | null;
  invokeSaveWidgetsResult: InvokeResult<null> | null;
  currentWidgets: DashboardWidget[];
  currentFilters: DashboardFilter[];
  remoteFilters: DashboardFilter[];
  remoteFilterOptions: Record<string, RemoteFilterOption[]>;
  /** True once loadPersistedFilters has completed (or determined there is nothing to restore). */
  persistedFiltersReady: boolean;
}

const initialState: State = {
  current: null,
  invokeCreateResult: null,
  invokeDeleteResult: null,
  invokeRenameResult: null,
  invokeDuplicateResult: null,
  list: null,
  invokeFetchWidgetsResult: null,
  invokeSaveWidgetsResult: null,
  currentWidgets: [],
  currentFilters: [],
  remoteFilters: [],
  remoteFilterOptions: {},
  persistedFiltersReady: false,
};

export type SaveCurrentAction = {
  type: 'saveCurrent' | 'dashboard/saveCurrent';
  payload: Pick<State, 'current'>;
};

type SaveInvokeCreateResultAction = {
  type: 'saveInvokeCreateResult';
  payload: Pick<State, 'invokeCreateResult'>;
};

type SaveInvokeDeleteResultAction = {
  type: 'saveInvokeDeleteResult';
  payload: Pick<State, 'invokeDeleteResult'>;
};

type SaveInvokeRenameResultAction = {
  type: 'saveInvokeRenameResult';
  payload: Pick<State, 'invokeRenameResult'>;
};

type SaveInvokeDuplicateResultAction = {
  type: 'saveInvokeDuplicateResult';
  payload: Pick<State, 'invokeDuplicateResult'>;
};

type SaveListAction = {
  type: 'saveList';
  payload: Pick<State, 'list'>;
};

export type SaveInvokeFetchWidgetsResultAction = {
  type: 'saveInvokeFetchWidgetsResult' | 'dashboard/saveInvokeFetchWidgetsResult';
  payload: Pick<State, 'invokeFetchWidgetsResult'>;
};

export type SaveCurrentWidgetsAction = {
  type: 'saveCurrentWidgets' | 'dashboard/saveCurrentWidgets';
  payload: { currentWidgets: DashboardWidget[] | ((prev: DashboardWidget[]) => DashboardWidget[]) };
};

export type SaveCurrentFiltersReducerAction = {
  type: 'dashboard/saveCurrentFiltersReducer';
  payload: Pick<State, 'currentFilters'>;
};

export type SaveCurrentFiltersAction = {
  type: 'dashboard/saveCurrentFilters';
  payload: Pick<State, 'currentFilters'>;
};

export type SaveRemoteFiltersReducerAction = {
  type: 'dashboard/saveRemoteFiltersReducer';
  payload: Pick<State, 'remoteFilters'>;
};

export type SaveRemoteFiltersAction = {
  type: 'dashboard/saveRemoteFilters';
  payload: Pick<State, 'remoteFilters'>;
};

export type SaveRemoteFilterOptionsAction = {
  type: 'saveRemoteFilterOptions' | 'dashboard/saveRemoteFilterOptions';
  payload: Pick<State, 'remoteFilterOptions'>;
};

export type UpdateSettingsEffect = {
  type: 'dashboard/updateSettings';
  payload: { settings: DashboardSettings; dashboardId: string; recalculate: boolean };
};

export type SaveInvokeSaveWidgetsResultAction = {
  type: 'saveInvokeSaveWidgetsResult' | 'dashboard/saveInvokeSaveWidgetsResult';
  payload: Pick<State, 'invokeSaveWidgetsResult'>;
};

export type GetDashboardEffect = {
  type: 'dashboard/get';
  payload: { id: string };
};

export type GetDashboardWidgetsEffect = {
  type: 'dashboard/getDashboardWidgets';
  payload: { id: string };
};

export type InvokeSaveWidgetsEffect = {
  type: 'dashboard/invokeSaveWidgets';
  payload: {
    dashboard: Dashboard;
    prevWidgets: DashboardWidget[];
    nextWidgets: DashboardWidget[];
  };
};

export type FetchRemoteFilterOptionsEffect = {
  type: 'dashboard/fetchRemoteFilterOptions';
  payload: {
    widgetId: string;
    columns: string[];
  };
};

export type LoadPersistedFiltersEffect = {
  type: 'dashboard/loadPersistedFilters';
  payload: { dashboardId: string };
};

export type PersistFiltersEffect = {
  type: 'dashboard/persistFilters';
};

export type ClearFilterPersistenceCacheEffect = {
  type: 'dashboard/clearFilterPersistenceCache';
  payload: { dashboardId: string };
};

export type SavePersistedFiltersReadyAction = {
  type: 'savePersistedFiltersReady' | 'dashboard/savePersistedFiltersReady';
  payload: Pick<State, 'persistedFiltersReady'>;
};

export function getFilterOperator(type: DataType): string {
  switch (type) {
    case 'date':
      return 'date_equal';
    case 'date_range':
      return 'date_between';
    case 'datetime':
      return 'datetime_equal';
    case 'datetime_range':
      return 'datetime_between';
    default:
      return 'equal';
  }
}

function buildDefaultFilterEntries(
  widget: DynamicFiltersDashboardWidget,
  filterConfig: FilterType,
): { localFilters: DashboardFilter[]; remoteFilters: DashboardFilter[] } {
  const localFilters: DashboardFilter[] = [];
  const remoteFilters: DashboardFilter[] = [];
  if (!filterConfig.defaultValue) return { localFilters, remoteFilters };
  const operator = getFilterOperator(filterConfig.type);
  const isRange = filterConfig.type === 'date_range' || filterConfig.type === 'datetime_range';
  const values: string[] = isRange
    ? filterConfig.defaultValue.split('|')
    : [filterConfig.defaultValue];
  filterConfig.columns.forEach((col) => {
    const [vizId, column, remote] = col.split('.');
    const entry: DashboardFilter = { widgetId: widget.id, vizId, column, operator, values };
    if (remote) {
      remoteFilters.push(entry);
    } else {
      localFilters.push(entry);
    }
  });
  return { localFilters, remoteFilters };
}

const debouncedPersist = debounce(
  (userId: string, dashboardId: string, options: FilterOptionsMap) =>
    DashboardFilterPersistence.save(userId, dashboardId, options),
  500,
);

/**
 * True when a settings save is a Cache_Mode_Crossing (Req 5.1/5.2/5.5/5.6): the
 * Managed_Mode membership flips (managed<->unmanaged), or the mode moves between
 * the two managed modes (auto<->custom). `off<->on` is never a crossing.
 */
export function isCacheModeCrossing(oldMode: DashboardCacheMode, newMode: DashboardCacheMode): boolean {
  const oldManaged = isManagedCacheMode(oldMode);
  const newManaged = isManagedCacheMode(newMode);
  // membership flipped (managed<->unmanaged) OR both managed but different (auto<->custom)
  return oldManaged !== newManaged || (oldManaged && newManaged && oldMode !== newMode);
}

/**
 * Deep, key-order-insensitive canonical serialization used to compare
 * FilterCombination values. Object keys are emitted in sorted order at every
 * depth so that `{ a: 1, b: 2 }` and `{ b: 2, a: 1 }` serialize identically.
 * Arrays keep their order (element order inside a single combination's filters
 * is meaningful); it is the top-level list of combinations that is compared
 * order-insensitively by the caller.
 */
function canonicalize(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalize).join(',')}]`;
  }
  const record = value as Record<string, unknown>;
  const entries = Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${canonicalize(record[key])}`);
  return `{${entries.join(',')}}`;
}

/**
 * Value-equality for the persisted Custom_Combinations, order-insensitive at both
 * the list level and the object-key level. Two combination lists are equal when
 * they contain the same set of combination VALUES regardless of list order or key
 * ordering within each combination. Undefined is treated as an empty list.
 */
function isEqualCombinations(
  a: FilterCombination[] | undefined,
  b: FilterCombination[] | undefined,
): boolean {
  const listA = (a ?? []).map(canonicalize).sort();
  const listB = (b ?? []).map(canonicalize).sort();
  if (listA.length !== listB.length) return false;
  return listA.every((item, i) => item === listB[i]);
}

/**
 * True when the mode did NOT cross but stayed in the SAME Managed_Mode and that
 * mode's relevant parameter changed vs the persisted settings (Req 6.7):
 *   - 'auto'   -> cacheAutoRecencyDays differs (defaulted via DEFAULT_CACHE_AUTO_RECENCY_DAYS)
 *   - 'custom' -> cacheCustomCombinations differ by value (order-insensitive)
 * For any other mode, or when the relevant parameter is unchanged, returns false.
 * Only meaningful when oldMode === newMode; callers OR this with isCacheModeCrossing,
 * which already covers the crossing cases (including auto<->custom).
 */
export function isManagedParamChange(
  oldMode: DashboardCacheMode,
  newMode: DashboardCacheMode,
  oldSettings: Pick<DashboardSettings, 'cacheAutoRecencyDays' | 'cacheCustomCombinations'>,
  newSettings: Pick<DashboardSettings, 'cacheAutoRecencyDays' | 'cacheCustomCombinations'>,
): boolean {
  if (oldMode !== newMode) return false; // crossings are handled by isCacheModeCrossing
  if (newMode === 'auto') {
    return (
      (oldSettings.cacheAutoRecencyDays ?? DEFAULT_CACHE_AUTO_RECENCY_DAYS) !==
      (newSettings.cacheAutoRecencyDays ?? DEFAULT_CACHE_AUTO_RECENCY_DAYS)
    );
  }
  if (newMode === 'custom') {
    return !isEqualCombinations(
      oldSettings.cacheCustomCombinations,
      newSettings.cacheCustomCombinations,
    );
  }
  return false;
}

/**
 * Build the Cache_Refresh_Config_Payload via Viz_Resolution over the dashboard's
 * widgets (Req 6). Keeps only VisualizationDashboardWidgets, resolves each one's
 * `vizId` and `datasetId`, and assembles the payload.
 *
 * `datasetId` resolution is INJECTED via `resolveDatasetId` so the builder stays
 * pure and unit-testable — the effect (task 5.2) passes the real resolver (Redux
 * viz-query result first, then service fallback). The resolver may be sync or
 * async; `await` handles both.
 */
export async function buildCacheRefreshConfigPayload(
  dashboardId: string,
  mode: DashboardCacheMode,
  widgets: DashboardWidget[],
  combinations: FilterCombination[] | undefined,
  autoRecencyDays: number,
  resolveDatasetId: (vizId: string) => Promise<string | undefined> | string | undefined,
): Promise<CacheRefreshConfigPayload> {
  // Extract the vizId for each visualization widget first (sync), so the
  // dataset-id resolutions can then run concurrently rather than in a loop.
  const pendingVizzes: { widgetId: string; vizId: string }[] = [];
  for (const w of widgets) {
    if (!(w instanceof VisualizationDashboardWidget)) continue;

    let vizId: string;
    try {
      vizId = w.getConfigurations().visualizationId;
    } catch (e) {
      // Req 6.5 — malformed widget config: exclude + log, keep walking.
      logger.warn({
        label: `${ns}.buildCacheRefreshConfigPayload.badConfig`,
        message: { widgetId: w.id },
        error: e,
      });
      continue;
    }

    pendingVizzes.push({ widgetId: w.id, vizId });
  }

  const resolvedDatasetIds = await Promise.all(
    pendingVizzes.map(({ vizId }) => resolveDatasetId(vizId)),
  );

  const vizzes: CacheRefreshConfigViz[] = [];
  pendingVizzes.forEach(({ widgetId, vizId }, i) => {
    const datasetId = resolvedDatasetIds[i];
    if (!datasetId) {
      // Req 6.5 — unresolved datasetId: exclude + log the offending widget.
      logger.warn({
        label: `${ns}.buildCacheRefreshConfigPayload.unresolvedDatasetId`,
        message: { widgetId, vizId },
      });
      return;
    }
    vizzes.push({ vizId, datasetId });
  });

  // Req 6.1 — dashboardId, mode, vizzes always present.
  const payload: CacheRefreshConfigPayload = { dashboardId, mode, vizzes };
  // Req 6.3/6.4 — filterCombinations present iff mode === 'custom'.
  if (mode === 'custom') payload.filterCombinations = combinations ?? [];
  // Req 6.6/6.7 — autoRecencyDays present iff mode === 'auto'.
  if (mode === 'auto') payload.autoRecencyDays = autoRecencyDays;
  return payload;
}

const DashboardModel: ReduxSagaModel<
  State,
  {
    saveCurrent: SaveCurrentAction;
    saveInvokeCreateResult: SaveInvokeCreateResultAction;
    saveInvokeDeleteResult: SaveInvokeDeleteResultAction;
    saveInvokeRenameResult: SaveInvokeRenameResultAction;
    saveInvokeDuplicateResult: SaveInvokeDuplicateResultAction;
    saveList: SaveListAction;
    saveInvokeFetchWidgetsResult: SaveInvokeFetchWidgetsResultAction;
    saveInvokeSaveWidgetsResult: SaveInvokeSaveWidgetsResultAction;
    saveCurrentWidgets: SaveCurrentWidgetsAction;
    saveCurrentFiltersReducer: SaveCurrentFiltersReducerAction;
    saveRemoteFiltersReducer: SaveRemoteFiltersReducerAction;
    saveRemoteFilterOptions: SaveRemoteFilterOptionsAction;
    savePersistedFiltersReady: SavePersistedFiltersReadyAction;
  }
> = {
  namespace: 'dashboard',
  state: initialState,
  effects: {
    *invokeCreate({ payload }: { payload: { name: string } }, { put }) {
      try {
        const workspaceId = yield* sgselect((s) => s.organization.currentWorkspace);
        assertIsString(workspaceId, 'BEEM250320114023');
        const dashboardId = yield* sgcall(() =>
          Dashboard.create({ name: payload.name, workspaceId }),
        );
        yield put<SaveInvokeCreateResultAction>({
          type: 'saveInvokeCreateResult',
          payload: {
            invokeCreateResult: {
              success: true,
              data: dashboardId,
            },
          },
        });
      } catch (e) {
        yield put<SaveInvokeCreateResultAction>({
          type: 'saveInvokeCreateResult',
          payload: {
            invokeCreateResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeCreate.error`),
            },
          },
        });
      }
    },
    *invokeRename({ payload }: { payload: { name: string; record: Dashboard } }, { put }) {
      try {
        yield* sgcall(() => Dashboard.update({ name: payload.name, id: payload.record.id }));
        yield put<SaveInvokeRenameResultAction>({
          type: 'saveInvokeRenameResult',
          payload: { invokeRenameResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeRenameResultAction>({
          type: 'saveInvokeRenameResult',
          payload: {
            invokeRenameResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeRename.error`),
            },
          },
        });
      }
    },
    *invokeDelete({ payload }: { payload: { id: string } }, { put }) {
      try {
        yield* sgcall(() => Dashboard.delete(payload.id));
        yield put<SaveInvokeDeleteResultAction>({
          type: 'saveInvokeDeleteResult',
          payload: { invokeDeleteResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeDeleteResultAction>({
          type: 'saveInvokeDeleteResult',
          payload: {
            invokeDeleteResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeDelete.error`),
            },
          },
        });
      }
    },
    *invokeDuplicate({ payload }: { payload: { id: string; name: string } }, { put }) {
      try {
        const newId = yield* sgcall(() => Dashboard.duplicate(payload.id, payload.name));
        yield put<SaveInvokeDuplicateResultAction>({
          type: 'saveInvokeDuplicateResult',
          payload: { invokeDuplicateResult: { success: true, data: newId } },
        });
      } catch (e) {
        yield put<SaveInvokeDuplicateResultAction>({
          type: 'saveInvokeDuplicateResult',
          payload: {
            invokeDuplicateResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeDuplicate.error`),
            },
          },
        });
      }
    },
    *get({ payload }: { payload: GetDashboardEffect['payload'] }, { put }) {
      try {
        const dashboard = yield* sgcall(() => Dashboard.getDashboardById(payload.id));

        yield put({
          type: 'saveCurrent',
          payload: { current: dashboard },
        });
      } catch (e) {
        handleUserError(e, `${ns}.getOne.error`);
        yield put({
          type: 'saveCurrent',
          payload: { current: null },
        });
      }
    },
    *getListOfDashboardInCurrentWorkspace(_, { put }) {
      try {
        const workspaceId = yield* sgselect((s) => s.organization.currentWorkspace);
        assertIsString(workspaceId, 'BEEM250320132521');

        const dashboards = yield* sgcall(() => Dashboard.getDashboardsByWorkspace(workspaceId));

        yield put<SaveListAction>({
          type: 'saveList',
          payload: { list: { success: true, data: dashboards } },
        });
      } catch (e) {
        yield put<SaveListAction>({
          type: 'saveList',
          payload: {
            list: {
              success: false,
              error: handleUserError(e, `${ns}.getListOfDashboardInCurrentWorkspace.error`),
            },
          },
        });
      }
    },
    *getDashboardWidgets({ payload }: { payload: GetDashboardWidgetsEffect['payload'] }, { put }) {
      const widgets = yield* sgcall(() =>
        DashboardWidgetFactory.getDashboardWidgetsByDashboard(payload.id),
      );
      try {
        yield put<SaveInvokeFetchWidgetsResultAction>({
          type: 'saveInvokeFetchWidgetsResult',
          payload: { invokeFetchWidgetsResult: { success: true, data: widgets } },
        });
      } catch (e) {
        yield put<SaveInvokeFetchWidgetsResultAction>({
          type: 'saveInvokeFetchWidgetsResult',
          payload: {
            invokeFetchWidgetsResult: {
              success: false,
              error: handleUserError(e, `${ns}.getDashboardWidgets.error`),
            },
          },
        });
      }
    },
    *invokeSaveWidgets(
      {
        payload: { dashboard, prevWidgets, nextWidgets },
      }: {
        payload: InvokeSaveWidgetsEffect['payload'];
      },
      { put },
    ) {
      try {
        yield* sgcall(() => dashboard.saveWidgets(nextWidgets, prevWidgets));
        // Req 5.3 — success path structurally unchanged.
        yield put<SaveInvokeSaveWidgetsResultAction>({
          type: 'saveInvokeSaveWidgetsResult',
          payload: { invokeSaveWidgetsResult: { success: true, data: null } },
        });
      } catch (e) {
        // Req 3.8 — saveWidgets failed: put failure result and return immediately.
        // No cleanup mutations are called.
        yield put<SaveInvokeSaveWidgetsResultAction>({
          type: 'saveInvokeSaveWidgetsResult',
          payload: {
            invokeSaveWidgetsResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeSaveWidgets.error`),
            },
          },
        });
        return;
      }

      // Req 3.1/3.10 — identify Removed_Widgets: VisualizationDashboardWidgets in prevWidgets
      // but not in nextWidgets, excluding those with state === 'new' (never persisted).
      const nextWidgetIds = new Set(nextWidgets.map((w) => w.id));
      const removedWidgets = prevWidgets.filter(
        (w): w is VisualizationDashboardWidget =>
          w instanceof VisualizationDashboardWidget &&
          !nextWidgetIds.has(w.id) &&
          w.state !== 'new',
      );

      // Req 3.2/3.3/3.5/3.6 — for each removed widget, perform Viz_Resolution then
      // call the mutation. Failures at either step are logged and skipped; they do not
      // affect other widgets or the already-committed save result.
      // Req 3.9 — calls are issued concurrently via Promise.allSettled.
      yield* sgcall(() =>
        Promise.allSettled(
          removedWidgets.map(async (w) => {
            let vizId: string;
            try {
              vizId = w.getConfigurations().visualizationId;
              // Guard against a missing/empty visualizationId: without this,
              // getVisualizationById below would query with a falsy id. Throw
              // so it is handled by the same badConfig path (log + skip widget).
              if (!vizId) throw new Error('missing visualizationId');
            } catch (e) {
              logger.error({
                label: `${ns}.invokeSaveWidgets.vizResolution.badConfig`,
                message: { widgetId: w.id },
                error: e,
              });
              return;
            }

            let datasetId: string;
            try {
              const viz = await Visualization.getVisualizationById(vizId);
              datasetId = viz.datasetId;
            } catch (e) {
              // Req 3.5 — Viz_Resolution failure: log, skip this widget, continue others.
              logger.error({
                label: `${ns}.invokeSaveWidgets.vizResolution.error`,
                message: { widgetId: w.id, vizId },
                error: e,
              });
              return;
            }

            try {
              // Req 3.3/3.4 — call mutation only after saveWidgets succeeded.
              // Req 3.6 — fire-and-forget: failure logged, does not affect save result.
              await Dashboard.notifyVizRemovedFromDashboard({
                datasetId,
                vizId,
                dashboardId: dashboard.id,
              });
            } catch (e) {
              logger.error({
                label: `${ns}.invokeSaveWidgets.notifyVizRemovedFromDashboard.error`,
                message: { widgetId: w.id, vizId, dashboardId: dashboard.id },
                error: e,
              });
            }
          }),
        ),
      );
    },
    *fetchRemoteFilterOptions(
      { payload }: { payload: FetchRemoteFilterOptionsEffect['payload'] },
      { put },
    ) {
      const { widgetId, columns } = payload;

      const invokeRunVisualizationQueryResult = yield* sgselect(
        (s) => s.visualization.invokeRunVisualizationQueryResultByVisualizationId,
      );

      const remoteColumnsToFetch: Array<{
        key: string;
        columnName: string;
        datasetId: string;
      }> = [];

      columns.forEach((colStr) => {
        const parts = colStr.split('.');
        if (parts.length >= 3 && parts[2] === 'remote') {
          const vizId = parts[0];
          const columnName = parts[1];
          const key = `${widgetId}.${vizId}.${columnName}`;

          const vizResult = invokeRunVisualizationQueryResult[vizId];

          if (vizResult?.success && vizResult.data.visualization) {
            remoteColumnsToFetch.push({
              key,
              columnName,
              datasetId: vizResult.data.visualization.datasetId,
            });
          }
        }
      });

      // Cache query results by datasetId+columnName to avoid running duplicate queries
      // when multiple visualizations reference the same dataset column.
      const queryCache = new Map<string, RemoteFilterOption[]>();

      // this `for-of loop with yield` ensures only 1 query runs at a time
      for (const { key, columnName, datasetId } of remoteColumnsToFetch) {
        const cacheKey = `${datasetId}::${columnName}`;

        if (queryCache.has(cacheKey)) {
          yield put<SaveRemoteFilterOptionsAction>({
            type: 'saveRemoteFilterOptions',
            payload: { remoteFilterOptions: { [key]: queryCache.get(cacheKey)! } },
          });
          continue;
        }

        try {
          const dataset = yield* sgcall(() => Dataset.getDatasetById(datasetId));
          const tableName = dataset.dbViewsQualifiedName;

          const distinctQuery = `SELECT DISTINCT "${columnName}" FROM ${tableName} WHERE "${columnName}" IS NOT NULL ORDER BY "${columnName}" LIMIT 1000`;

          const { executionId } = yield* sgcall(() =>
            Dataset.testSqlQuery(distinctQuery, 1000, 0, [], []),
          );

          const { rows } = yield* sgcall(() =>
            waitForRedshiftQueryUntilDoneAndReturnResults({ executionId }),
          );

          if (!rows || !Array.isArray(rows)) {
            queryCache.set(cacheKey, []);
            yield put<SaveRemoteFilterOptionsAction>({
              type: 'saveRemoteFilterOptions',
              payload: { remoteFilterOptions: { [key]: [] } },
            });
            continue;
          }

          const options: RemoteFilterOption[] = rows.map((row) => {
            let val = row[columnName];
            if (val === undefined) {
              // case-insensitive match
              const actualKey = Object.keys(row).find(
                (k) => k.toLowerCase() === columnName.toLowerCase(),
              );
              if (actualKey) {
                val = row[actualKey];
              }
            }
            return { value: val === null ? null : String(val), label: String(val) };
          });
          queryCache.set(cacheKey, options);
          yield put<SaveRemoteFilterOptionsAction>({
            type: 'saveRemoteFilterOptions',
            payload: { remoteFilterOptions: { [key]: options } },
          });
        } catch (error) {
          logger.error({
            label: `${ns}.fetchRemoteFilterOptions.error`,
            message: { key, error },
            error,
          });
          queryCache.set(cacheKey, []);
          yield put<SaveRemoteFilterOptionsAction>({
            type: 'saveRemoteFilterOptions',
            payload: { remoteFilterOptions: { [key]: [] } },
          });
        }
      }
    },
    *updateSettings({ payload }: { payload: UpdateSettingsEffect['payload'] }, { put }) {
      const current = yield* sgselect((s) => s.dashboard.current);
      if (!current) return;
      const oldRowHeight = current.settings.rowHeight;
      const newRowHeight = payload.settings.rowHeight;
      // Capture the cache modes BEFORE the save so crossing detection compares the
      // persisted-before value against the requested value (Req 5.1/5.2).
      const oldMode = current.settings.cacheMode;
      const newMode = payload.settings.cacheMode;

      try {
        yield* sgcall(() => Dashboard.updateSettings(payload.dashboardId, payload.settings));
      } catch (e) {
        // Req 4.4 — save failed: optimistically retain ONLY the cache fields
        // (cacheMode/cacheCustomCombinations/cacheAutoRecencyDays) in state, not rolled
        // back, and surface the error to the editor. Non-cache fields (columns, gap,
        // rowHeight, etc.) are intentionally left at their pre-save values so a failed
        // save does not silently apply them — the design scopes the optimistic-on-failure
        // update to the crossing/combination fields only. Send NO cache-refresh config
        // request (Req 5.3).
        const optimisticSettings: DashboardSettings = {
          ...current.settings,
          cacheMode: payload.settings.cacheMode,
          cacheCustomCombinations: payload.settings.cacheCustomCombinations,
          // Guard against an undefined raw form value (older/unmigrated dashboard or a
          // non-'auto' submission that omits the field); fall back to the pre-save value.
          cacheAutoRecencyDays:
            payload.settings.cacheAutoRecencyDays ?? current.settings.cacheAutoRecencyDays,
        };
        yield put<SaveCurrentAction>({
          type: 'saveCurrent',
          payload: { current: current.withSettings(optimisticSettings) },
        });
        handleUserError(e, `${ns}.updateSettings.error`);
        return;
      }

      // Req 4.3 — reflect the saved mode + combinations + recency window in state.
      yield put<SaveCurrentAction>({
        type: 'saveCurrent',
        payload: { current: current.withSettings(payload.settings) },
      });

      // Rescale widget y/h so visual size stays the same after row height change
      if (payload.recalculate && oldRowHeight !== newRowHeight) {
        const ratio = oldRowHeight / newRowHeight;
        const currentWidgets: DashboardWidget[] = yield* sgselect(
          (s) => s.dashboard.currentWidgets,
        );
        const rescaled = currentWidgets.map((w) => {
          w.position = {
            ...w.position,
            y: Math.round(w.position.y * ratio),
            h: Math.max(1, Math.round(w.position.h * ratio)),
          };
          w.state = w.state === 'new' ? 'new' : 'dirty';
          return w;
        });
        yield put<SaveCurrentWidgetsAction>({
          type: 'saveCurrentWidgets',
          payload: { currentWidgets: rescaled },
        });
      }

      // Req 5.1/5.4 — only after a successful save, and only on a Cache_Mode_Crossing,
      // send exactly one cache-refresh config request to beem-agent.
      if (
        isCacheModeCrossing(oldMode, newMode) ||
        isManagedParamChange(oldMode, newMode, current.settings, payload.settings)
      ) {
        const currentWidgets: DashboardWidget[] = yield* sgselect(
          (s) => s.dashboard.currentWidgets,
        );
        const resultByVizId = yield* sgselect(
          (s) => s.visualization.invokeRunVisualizationQueryResultByVisualizationId,
        );

        // Resolve datasetId per viz up-front so any service fallback runs inside a
        // saga call (sgcall). Redux viz-query result first, then service fallback.
        const datasetIdByVizId = new Map<string, string | undefined>();
        for (const w of currentWidgets) {
          if (!(w instanceof VisualizationDashboardWidget)) continue;
          let vizId: string;
          try {
            vizId = w.getConfigurations().visualizationId;
          } catch {
            // Malformed config is handled/logged inside the builder; skip resolving here.
            continue;
          }
          if (datasetIdByVizId.has(vizId)) continue;

          const reduxResult = resultByVizId[vizId];
          if (reduxResult?.success) {
            datasetIdByVizId.set(vizId, reduxResult.data.visualization.datasetId);
            continue;
          }
          try {
            const viz = yield* sgcall(() => Visualization.getVisualizationById(vizId));
            datasetIdByVizId.set(vizId, viz.datasetId);
          } catch (e) {
            // Lookup failure → unresolved; builder will exclude + log this widget.
            logger.warn({
              label: `${ns}.updateSettings.resolveDatasetId.error`,
              message: { vizId },
              error: e,
            });
            datasetIdByVizId.set(vizId, undefined);
          }
        }

        const payloadOut = yield* sgcall(() =>
          buildCacheRefreshConfigPayload(
            payload.dashboardId,
            newMode,
            currentWidgets,
            payload.settings.cacheCustomCombinations,
            // payload.settings is the raw form submission; cacheAutoRecencyDays can be
            // undefined for an older/unmigrated dashboard or when the modal omits the field
            // (it only renders in 'auto' mode). Default here so a real number is always
            // passed — the value is only actually used when mode === 'auto'.
            payload.settings.cacheAutoRecencyDays ?? DEFAULT_CACHE_AUTO_RECENCY_DAYS,
            (vizId) => datasetIdByVizId.get(vizId),
          ),
        );

        try {
          // Req 5.1 — exactly one request.
          yield* sgcall(() => Dashboard.setCacheRefreshConfig(payloadOut));
        } catch (e) {
          // A config-request failure must NEVER surface as a settings-save failure;
          // log only (design Error Handling).
          logger.error({
            label: `${ns}.updateSettings.setCacheRefreshConfig.error`,
            message: e,
            error: e,
          });
        }
      }
    },
    *loadPersistedFilters(
      { payload }: { payload: LoadPersistedFiltersEffect['payload'] },
      { put },
    ) {
      try {
        const userId = yield* sgselect((s) => s.user.currentUser?.id);
        if (!userId) {
          yield put<SavePersistedFiltersReadyAction>({
            type: 'savePersistedFiltersReady',
            payload: { persistedFiltersReady: true },
          });
          return;
        }
        const currentWidgets = yield* sgselect((s) => s.dashboard.currentWidgets);
        const optionsMap = yield* sgcall(() =>
          DashboardFilterPersistence.load(userId, payload.dashboardId),
        );
        if (!optionsMap) {
          // No persisted record — still apply default values if any filters have them.
          const defaultLocalFilters: DashboardFilter[] = [];
          const defaultRemoteFilters: DashboardFilter[] = [];

          for (const widget of currentWidgets) {
            if (!(widget instanceof DynamicFiltersDashboardWidget)) continue;
            let filterConfigs: FilterType[];
            try {
              filterConfigs = widget.getConfigurations().filters;
            } catch {
              continue;
            }
            filterConfigs.slice(0, DASHBOARD_MAX_VISIBLE_FILTERS).forEach((filterConfig) => {
              const { localFilters, remoteFilters } = buildDefaultFilterEntries(
                widget,
                filterConfig,
              );
              defaultLocalFilters.push(...localFilters);
              defaultRemoteFilters.push(...remoteFilters);
            });
          }

          if (defaultLocalFilters.length > 0) {
            yield put<SaveCurrentFiltersReducerAction>({
              type: 'dashboard/saveCurrentFiltersReducer',
              payload: { currentFilters: defaultLocalFilters },
            });
          }
          if (defaultRemoteFilters.length > 0) {
            yield put<SaveRemoteFiltersReducerAction>({
              type: 'dashboard/saveRemoteFiltersReducer',
              payload: { remoteFilters: defaultRemoteFilters },
            });
          }
          yield put<SavePersistedFiltersReadyAction>({
            type: 'savePersistedFiltersReady',
            payload: { persistedFiltersReady: true },
          });
          return;
        }

        const newLocalFilters: DashboardFilter[] = [];
        const newRemoteFilters: DashboardFilter[] = [];

        for (const [mapKey, rawValue] of Object.entries(optionsMap)) {
          const hashIdx = mapKey.indexOf('#');
          if (hashIdx === -1) continue;
          const widgetId = mapKey.slice(0, hashIdx);
          const fieldName = mapKey.slice(hashIdx + 1);
          const filterIdx = parseInt(fieldName.replace('filter-', ''), 10);
          if (isNaN(filterIdx)) continue;

          const widget = currentWidgets.find((w) => w.id === widgetId);
          if (!(widget instanceof DynamicFiltersDashboardWidget)) continue;

          let filterConfig: FilterType | undefined;
          try {
            filterConfig = widget.getConfigurations().filters[filterIdx];
          } catch {
            continue;
          }
          if (!filterConfig) continue;

          const normalised: unknown[] = Array.isArray(rawValue)
            ? (rawValue as unknown[])
            : [rawValue];

          const operator = getFilterOperator(filterConfig.type);

          filterConfig.columns.forEach((col) => {
            const [vizId, column, remote] = col.split('.');
            const entry: DashboardFilter = {
              widgetId,
              vizId,
              column,
              operator,
              values: normalised,
            };
            if (remote) {
              newRemoteFilters.push(entry);
            } else {
              newLocalFilters.push(entry);
            }
          });
        }

        // Apply default values for filter fields that have no persisted value.
        // Persisted values take priority — defaults only fill gaps.
        for (const widget of currentWidgets) {
          if (!(widget instanceof DynamicFiltersDashboardWidget)) continue;
          let filterConfigs: FilterType[];
          try {
            filterConfigs = widget.getConfigurations().filters;
          } catch {
            continue;
          }
          filterConfigs.slice(0, DASHBOARD_MAX_VISIBLE_FILTERS).forEach((filterConfig) => {
            if (!filterConfig.defaultValue) return;
            // skip if a persisted value already covers this widget+field
            const alreadyCovered = [...newLocalFilters, ...newRemoteFilters].some(
              (f) =>
                f.widgetId === widget.id &&
                filterConfig.columns.some((col) => {
                  const [vizId, column] = col.split('.');
                  return f.vizId === vizId && f.column === column;
                }),
            );
            if (alreadyCovered) return;

            const { localFilters, remoteFilters } = buildDefaultFilterEntries(widget, filterConfig);
            newLocalFilters.push(...localFilters);
            newRemoteFilters.push(...remoteFilters);
          });
        }

        yield put<SaveCurrentFiltersReducerAction>({
          type: 'dashboard/saveCurrentFiltersReducer',
          payload: { currentFilters: newLocalFilters },
        });
        yield put<SaveRemoteFiltersReducerAction>({
          type: 'dashboard/saveRemoteFiltersReducer',
          payload: { remoteFilters: newRemoteFilters },
        });
        yield put<SavePersistedFiltersReadyAction>({
          type: 'savePersistedFiltersReady',
          payload: { persistedFiltersReady: true },
        });
      } catch (e) {
        logger.error({ label: `${ns}.loadPersistedFilters.error`, message: e, error: e });
        yield put<SavePersistedFiltersReadyAction>({
          type: 'savePersistedFiltersReady',
          payload: { persistedFiltersReady: true },
        });
      }
    },
    *persistFilters() {
      try {
        const userId = yield* sgselect((s) => s.user.currentUser?.id);
        if (!userId) return;
        const dashboardId = yield* sgselect((s) => s.dashboard.current?.id);
        if (!dashboardId) return;
        const currentWidgets = yield* sgselect((s) => s.dashboard.currentWidgets);
        const currentFilters = yield* sgselect((s) => s.dashboard.currentFilters);
        const remoteFilters = yield* sgselect((s) => s.dashboard.remoteFilters);

        const allFilters = [...currentFilters, ...remoteFilters];
        const optionsMap: FilterOptionsMap = {};

        for (const widget of currentWidgets) {
          if (!(widget instanceof DynamicFiltersDashboardWidget)) continue;
          let filterConfigs: FilterType[];
          try {
            filterConfigs = widget.getConfigurations().filters;
          } catch {
            continue;
          }
          filterConfigs.slice(0, DASHBOARD_MAX_VISIBLE_FILTERS).forEach((filterConfig, idx) => {
            const fieldKey = `${widget.id}#filter-${idx}`;
            const matchingFilters = allFilters.filter(
              (f) =>
                f.widgetId === widget.id &&
                filterConfig.columns.some((col) => {
                  const [vizId, column] = col.split('.');
                  return f.vizId === vizId && f.column === column;
                }),
            );
            if (matchingFilters.length === 0) return;
            const values = matchingFilters[0].values;
            if (!values || values.length === 0) return;
            const formValue = values.length === 1 ? values[0] : values;
            if (formValue === null || formValue === undefined || formValue === '') return;
            optionsMap[fieldKey] = formValue;
          });
        }

        debouncedPersist(userId, dashboardId, optionsMap);
      } catch (e) {
        logger.error({ label: `${ns}.persistFilters.error`, message: e, error: e });
      }
    },
    *clearFilterPersistenceCache({
      payload,
    }: {
      payload: ClearFilterPersistenceCacheEffect['payload'];
    }) {
      try {
        const userId = yield* sgselect((s) => s.user.currentUser?.id);
        if (!userId) return;
        DashboardFilterPersistence.clearCache(userId, payload.dashboardId);
      } catch (e) {
        logger.error({ label: `${ns}.clearFilterPersistenceCache.error`, message: e, error: e });
      }
    },
    *saveCurrentFilters({ payload }: { payload: SaveCurrentFiltersAction['payload'] }, { put }) {
      yield put({ type: 'saveCurrentFiltersReducer', payload });
      yield put({ type: 'persistFilters', payload: {} });
    },
    *saveRemoteFilters({ payload }: { payload: SaveRemoteFiltersAction['payload'] }, { put }) {
      yield put({ type: 'saveRemoteFiltersReducer', payload });
      yield put({ type: 'persistFilters', payload: {} });
    },
  },
  reducers: {
    resetAll() {
      return { ...initialState };
    },
    saveCurrent(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeCreateResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeRenameResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeDeleteResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeDuplicateResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveList(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeFetchWidgetsResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeSaveWidgetsResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveCurrentWidgets(state, { payload: { currentWidgets: p } }) {
      if (typeof p !== 'function') return { ...state, currentWidgets: p };
      return { ...state, currentWidgets: p(state.currentWidgets) };
    },
    saveCurrentFiltersReducer(state, { payload }) {
      return { ...state, ...payload };
    },
    saveRemoteFiltersReducer(state, { payload }) {
      return { ...state, ...payload };
    },
    saveRemoteFilterOptions(state, { payload }) {
      return {
        ...state,
        remoteFilterOptions: { ...state.remoteFilterOptions, ...payload.remoteFilterOptions },
      };
    },
    savePersistedFiltersReady(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default DashboardModel;
